/*
 * @Description: 神策埋点统一上报工具集
 * @FilePath: /utils/sensors.js
 */

import { advanceAnalyticsIdentity } from '@/reporting/earlyEvents';
import { ReportingCenter } from '@/reporting/reportingCenter';
import { ProviderType } from '@/reporting/types';
import { generateEventId } from '@/reporting/utils/eventIdGenerator';
import logger from '@/utils/client-logger';

/**
 * @desc 获取神策实例
 * @returns {object} sensors 实例
 */
export const getSensors = () => {
  if (window.sensors) {
    return window.sensors;
  }

  if (window.sensorsDataAnalytic201505) {
    return window.sensorsDataAnalytic201505;
  }

  if (window.parent.sensors) {
    return window.parent.sensors;
  }

  if (window.parent.sensorsDataAnalytic201505) {
    return window.parent.sensorsDataAnalytic201505;
  }
  return {};
};

/**
 * @desc 普通埋点上报，并将旧神策链路旁路镜像到 Snowplow
 * @param {string} eventName 事件名称
 * @param {object} params 事件参数
 */
export const sensorsTrack = (eventName, params = {}) => {
  try {
    const _params: Record<string, unknown> = {};

    // 根据标准：只要是值为 '', null, undefined, 'null', 'undefined', []
    Object.keys(params).forEach((key) => {
      const v = params[key];
      if (
        v !== '' &&
        v !== 'null' &&
        v !== 'undefined' &&
        v !== null &&
        v !== undefined &&
        !(v instanceof Array && !v.length)
      ) {
        _params[key] = v;
      }
    });

    const eventId = generateEventId();
    _params.sensors_event_id = eventId;

    // ReportingCenter 负责同时接收 Sensors 与 Snowplow，SDK 未加载不会丢弃。
    return ReportingCenter.getInstance().track({ eventName, properties: _params, eventId }, [
      ProviderType.SENSORS,
    ]);
  } catch {
    // 不把业务参数或第三方异常中的敏感内容写入日志。
    logger.error('Sensors capture failed');
    return { accepted: false, reason: 'capture_failed' };
  }
};

/**
 * @desc 神策登录事件
 * @param {string} uuid 用户唯一标识
 */
export const sensorsLogin = (uuid: string) => {
  // 身份命令与早期事件保持相同顺序，避免登录后的身份覆盖历史事件。
  if (!uuid) return { accepted: false, reason: 'invalid_identity' };
  try {
    return advanceAnalyticsIdentity({ kind: 'login', uuid });
  } catch {
    logger.error('Sensors identity capture failed');
    return { accepted: false, reason: 'capture_failed' };
  }
};

/** 将登出交给有序身份流，保留旧链路的 profile-only 登出规则。 */
export const sensorsLogout = () => {
  try {
    return advanceAnalyticsIdentity({ kind: 'logout' });
  } catch {
    logger.error('Sensors identity capture failed');
    return { accepted: false, reason: 'capture_failed' };
  }
};

/**
 * @desc 检查 sensors 是否已初始化
 * @returns {boolean} 是否可用
 */
export const isSensorsAvailable = () => {
  const sensors = getSensors();
  return sensors && Object.keys(sensors).length > 0;
};
